import { useEffect, useId, useRef, useState } from "react";
import { Download, Loader2, Pause, Play } from "lucide-react";

const BAR_COUNT = 72;
const EMPTY_BARS = Array(BAR_COUNT).fill(2);
// A still, speech-shaped placeholder until the generated audio supplies its peaks.
const LOADING_BARS = [
  28, 20, 8, 15, 23, 11, 5, 3, 18, 27, 14, 7,
  21, 9, 4, 26, 16, 10, 3, 13, 22, 8, 17, 11,
  5, 19, 25, 16, 29, 20, 12, 6, 3, 9, 18, 14,
  7, 23, 16, 27, 20, 30, 24, 12, 18, 8, 4, 26,
  21, 10, 5, 15, 9, 3, 18, 28, 14, 22, 8, 17,
  24, 13, 19, 10, 6, 4, 12, 7, 3, 25, 14, 8,
];

const formatTime = (seconds) => {
  const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, "0")}`;
};

// Summarize the actual audio samples; the waveform stays still during playback.
const getBars = (buffer) => {
  const energies = Array(BAR_COUNT).fill(0);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const samples = buffer.getChannelData(channel);
    for (let bar = 0; bar < BAR_COUNT; bar += 1) {
      const start = Math.floor((bar * samples.length) / BAR_COUNT);
      const end = Math.floor(((bar + 1) * samples.length) / BAR_COUNT);
      let sum = 0;
      for (let index = start; index < end; index += 1) sum += samples[index] ** 2;
      energies[bar] = Math.max(energies[bar], Math.sqrt(sum / Math.max(1, end - start)));
    }
  }
  const peak = Math.max(...energies);
  return energies.map((energy) => peak ? Math.max(2, (energy / peak) * 30) : 2);
};

export default function SpeechWaveformPlayer({ audioUrl, audioBlob, audioRef, disabled = false, isGenerating = false, onBeforePlay, onInteraction, onError }) {
  const clipId = useId().replace(/:/g, "");
  const requestRef = useRef(0);
  const seekPointerRef = useRef(false);
  const [showSeekFocus, setShowSeekFocus] = useState(false);
  const [bars, setBars] = useState(EMPTY_BARS);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [error, setError] = useState("");
  const [readyUrl, setReadyUrl] = useState(null);
  const callbacksRef = useRef({ onInteraction, onError });

  useEffect(() => {
    callbacksRef.current = { onInteraction, onError };
  }, [onInteraction, onError]);

  useEffect(() => {
    const audio = audioRef.current;
    const updateTime = () => {
      setCurrentTime(audio.currentTime || 0);
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const handlePlay = () => {
      setIsPlaying(true);
      setError("");
      callbacksRef.current.onInteraction?.();
    };
    const handlePause = () => { setIsPlaying(false); setIsWaiting(false); };
    const handleWaiting = () => setIsWaiting(true);
    const handleReady = () => { setIsWaiting(false); setReadyUrl(audioUrl); updateTime(); };
    const handleError = () => {
      setError("Audio could not be loaded. Generate speech again.");
      handlePause();
      callbacksRef.current.onError?.();
    };
    const events = {
      timeupdate: updateTime, durationchange: updateTime, loadedmetadata: updateTime,
      play: handlePlay, pause: handlePause, ended: handlePause,
      waiting: handleWaiting, playing: handleReady, loadeddata: handleReady, canplay: handleReady, error: handleError,
    };
    setReadyUrl(null);
    setError("");
    setIsPlaying(false);
    setIsWaiting(false);
    if (!audioUrl) {
      setCurrentTime(0);
      setDuration(0);
      return;
    }
    Object.entries(events).forEach(([event, handler]) => audio.addEventListener(event, handler));
    if (audio.getAttribute("src") !== audioUrl) {
      audio.src = audioUrl;
      audio.load();
    }
    updateTime();
    if (audio.error) handleError();
    else if (audio.readyState >= 2) handleReady();
    return () => {
      requestRef.current += 1;
      Object.entries(events).forEach(([event, handler]) => audio.removeEventListener(event, handler));
      audio.pause();
    };
  }, [audioRef, audioUrl]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setBars(EMPTY_BARS);
    if (!audioUrl) return;
    const decode = async () => {
      try {
        // An offline context avoids opening an audio device just to draw peaks.
        const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OfflineContext) return;
        const blob = audioBlob || await fetch(audioUrl, { signal: controller.signal }).then((response) => {
          if (!response.ok) throw new Error("Unable to read waveform");
          return response.blob();
        });
        const data = await blob.arrayBuffer();
        if (cancelled) return;
        const context = new OfflineContext(1, 1, 44100);
        const buffer = await context.decodeAudioData(data);
        if (!cancelled) setBars(getBars(buffer));
      } catch (_) {
        // Remote audio may play without allowing CORS decoding. Keep playback
        // and seeking available with a neutral track when peaks are unavailable.
      }
    };
    decode();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [audioBlob, audioUrl]);

  useEffect(() => {
    if (disabled || isGenerating) {
      requestRef.current += 1;
      audioRef.current.pause();
      setIsPlaying(false);
      setIsWaiting(false);
      setShowSeekFocus(false);
    }
  }, [audioRef, disabled, isGenerating]);

  const isLoading = isGenerating || Boolean(audioUrl && readyUrl !== audioUrl && !error);
  const controlsDisabled = disabled || isLoading || !audioUrl;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (controlsDisabled) return;
    if (!audio.paused || isWaiting) {
      requestRef.current += 1;
      audio.pause();
      setIsWaiting(false);
      return;
    }
    const request = ++requestRef.current;
    setError("");
    setIsWaiting(true);
    try {
      onBeforePlay?.();
      if (audio.ended) audio.currentTime = 0;
      await audio.play();
      if (requestRef.current === request) setIsWaiting(false);
    } catch (playError) {
      if (requestRef.current === request) {
        setIsWaiting(false);
        setIsPlaying(false);
        if (playError.name !== "AbortError") {
          setError("Unable to play audio. Try again.");
          callbacksRef.current.onError?.();
        }
      }
    }
  };

  const progress = !isGenerating && duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const displayBars = isLoading || !audioUrl ? LOADING_BARS : bars;
  const playbackLabel = isPlaying || isWaiting ? "Pause generated speech" : "Play generated speech";

  return (
    <div role="group" aria-label="Generated speech player" aria-busy={isLoading} className="w-full min-w-0 flex-1 basis-[280px] sm:max-w-xl">
      <div className={`flex min-w-0 items-center gap-3 rounded-xl border bg-gray-50 px-3 py-2 dark:bg-gray-800 ${error ? "border-red-300 dark:border-red-800" : "border-gray-200 dark:border-gray-700"}`}>
        <button type="button" onClick={togglePlayback} disabled={controlsDisabled} aria-label={playbackLabel} title={playbackLabel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white transition-colors enabled:hover:bg-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-gray-800">
          {isWaiting ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : isPlaying ? <Pause aria-hidden="true" className="h-4 w-4" /> : <Play aria-hidden="true" className="ml-0.5 h-4 w-4" />}
        </button>
        <div className="relative h-8 min-w-0 flex-1 rounded">
          <input type="range" aria-label="Seek generated speech" aria-valuetext={isLoading ? "Loading generated speech" : `${formatTime(currentTime)} of ${formatTime(duration)}`}
            min="0" max={duration || 1} step="0.1" value={isGenerating ? 0 : Math.min(currentTime, duration) || 0} disabled={controlsDisabled || !duration}
            onPointerDown={() => {
              seekPointerRef.current = true;
              setShowSeekFocus(false);
              if (!controlsDisabled && duration) callbacksRef.current.onInteraction?.();
            }}
            onFocus={() => setShowSeekFocus(!seekPointerRef.current)}
            onKeyDown={() => { seekPointerRef.current = false; setShowSeekFocus(true); }}
            onBlur={() => { seekPointerRef.current = false; setShowSeekFocus(false); }}
            onChange={(event) => {
              if (controlsDisabled || !duration) return;
              const time = Number(event.target.value);
              audioRef.current.currentTime = time;
              setCurrentTime(time);
              callbacksRef.current.onInteraction?.();
            }}
            className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed" />
          <svg aria-hidden="true" viewBox={`0 0 ${BAR_COUNT * 5} 32`} preserveAspectRatio="none" className={`h-full w-full rounded text-gray-300 dark:text-gray-600 ${showSeekFocus ? "ring-2 ring-orange-500" : ""}`}>
            <defs><clipPath id={clipId}><rect width={`${progress}%`} height="32" /></clipPath></defs>
            <g fill="currentColor">{displayBars.map((height, index) => <rect key={index} x={index * 5} y={(32 - height) / 2} width="3" height={height} rx="1.5" />)}</g>
            <g clipPath={`url(#${clipId})`} className="text-orange-500" fill="currentColor">{displayBars.map((height, index) => <rect key={index} x={index * 5} y={(32 - height) / 2} width="3" height={height} rx="1.5" />)}</g>
          </svg>
        </div>
        {!isLoading && <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{`${formatTime(currentTime)} / ${formatTime(duration)}`}</span>}
        {isLoading ? (
          <span role="status" aria-label={isGenerating ? "Generating speech" : "Loading generated speech"} className="flex h-8 w-8 shrink-0 items-center justify-center text-orange-500">
            <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />
          </span>
        ) : controlsDisabled ? (
          <button type="button" disabled aria-label="Download generated speech" className="flex h-8 w-8 shrink-0 cursor-not-allowed items-center justify-center text-gray-400 opacity-50">
            <Download aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : (
          <a href={audioUrl} download="generated-speech.mp3" target="_blank" rel="noopener noreferrer" aria-label="Download generated speech" title="Download generated speech"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-200 hover:text-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:text-gray-400 dark:hover:bg-gray-700">
          <Download aria-hidden="true" className="h-4 w-4" />
          </a>
        )}
      </div>
      {error && !isGenerating && <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
